/**
 * THERAPEUTIC TOOLS MARKETPLACE MODEL
 * 
 * Sistema de herramientas terapéuticas disponibles para diferentes
 * corrientes psicológicas y enfoques terapéuticos.
 * 
 * Incluye:
 * - Catálogo de herramientas disponibles
 * - Solicitudes de nuevas herramientas (feedback de usuarios)
 * - Categorización por corriente/enfoque terapéutico
 */

import { TherapeuticApproach } from '../therapist/therapist.model';

// ============================================
// ENUMS
// ============================================

export enum ToolCategory {
  ASSESSMENT = 'assessment',           // Evaluación/diagnóstico
  INTERVENTION = 'intervention',        // Intervención terapéutica
  VISUALIZATION = 'visualization',      // Visualización (genograma, etc)
  TRACKING = 'tracking',                // Seguimiento/monitoreo
  COMMUNICATION = 'communication',      // Comunicación terapeuta-paciente
  PSYCHOEDUCATION = 'psychoeducation',  // Psicoeducación
  SELF_HELP = 'self_help',              // Autoayuda/prácticas
  COACHING = 'coaching',                // Herramientas de coaching
  MINDFULNESS = 'mindfulness',          // Mindfulness/meditación
  OTHER = 'other'
}

export enum ToolStatus {
  AVAILABLE = 'available',              // Disponible para usar
  COMING_SOON = 'coming_soon',          // En desarrollo
  BETA = 'beta',                        // En fase beta
  DEPRECATED = 'deprecated'             // Obsoleto
}

export enum RequestStatus {
  PENDING = 'pending',                  // Pendiente de revisión
  UNDER_REVIEW = 'under_review',        // En revisión
  APPROVED = 'approved',                // Aprobada para desarrollo
  IN_DEVELOPMENT = 'in_development',    // En desarrollo
  COMPLETED = 'completed',              // Completada/disponible
  REJECTED = 'rejected'                 // Rechazada
}

export enum RequestPriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical'
}

// ============================================
// THERAPEUTIC TOOL
// ============================================

export interface TherapeuticTool {
  id: string;
  
  // Información básica
  name: string;
  slug: string;                         // URL-friendly name
  description: string;
  longDescription?: string;
  icon: string;                         // Emoji o URL de icono
  
  // Categorización
  category: ToolCategory;
  subcategory?: string;
  
  // Enfoques terapéuticos compatibles
  therapeuticApproaches: TherapeuticApproach[];
  allApproaches: boolean;               // Si aplica a todos los enfoques
  
  // Estado y disponibilidad
  status: ToolStatus;
  version: string;
  releaseDate?: string;
  
  // Navegación en la app
  route?: string;                       // Ruta interna de la app
  externalUrl?: string;                 // URL externa si aplica
  
  // Características
  features: string[];
  
  // Requisitos
  requiresPatient: boolean;             // Requiere tener un paciente
  requiresSubscription: boolean;        // Requiere suscripción de pago
  minimumPlan?: 'free' | 'basic' | 'pro' | 'enterprise';
  
  // Media
  screenshots?: string[];
  videoUrl?: string;
  tutorialUrl?: string;
  
  // Métricas
  usageCount?: number;
  averageRating?: number;
  totalRatings?: number;
  
  // Metadata
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

// ============================================
// TOOL REQUEST (Solicitud de nueva herramienta)
// ============================================

export interface ToolRequest {
  id: string;
  
  // Solicitante
  requesterId: string;
  requesterName?: string;
  requesterEmail?: string;
  requesterRole: 'therapist' | 'patient' | 'school' | 'admin';
  
  // Información del terapeuta (si aplica)
  therapistInfo?: {
    therapeuticApproach: TherapeuticApproach;
    specializations: string[];
    yearsExperience?: number;
  };
  
  // Detalles de la solicitud
  toolName: string;
  description: string;
  detailedDescription?: string;
  
  // Caso de uso
  useCase: string;                      // ¿Para qué lo usarías?
  targetUsers: ('therapist' | 'patient' | 'both')[];
  frequency?: string;                   // ¿Con qué frecuencia lo usarías?
  
  // Categorización sugerida
  suggestedCategory?: ToolCategory;
  suggestedApproaches?: TherapeuticApproach[];
  
  // Referencias
  references?: string[];                // URLs o nombres de herramientas similares
  attachments?: string[];               // URLs de archivos adjuntos
  
  // Estado de la solicitud
  status: RequestStatus;
  priority: RequestPriority;
  
  // Gestión interna
  internalNotes?: string;               // Notas del equipo
  assignedTo?: string;                  // ID del responsable
  estimatedDelivery?: string;
  
  // Votos/apoyo de la comunidad
  votes: number;
  voters: string[];                     // IDs de usuarios que votaron
  
  // Comunicación
  adminResponse?: string;
  responseDate?: string;
  
  // Resultado
  resultingToolId?: string;             // ID de la herramienta si se desarrolla
  
  // Metadata
  createdAt: string;
  updatedAt: string;
}

// ============================================
// TOOL RATING
// ============================================

export interface ToolRating {
  id: string;
  toolId: string;
  userId: string;
  userRole: 'therapist' | 'patient';
  
  rating: number;                       // 1-5
  review?: string;
  
  // Aspectos específicos
  easeOfUse?: number;                   // 1-5
  usefulness?: number;                  // 1-5
  wouldRecommend?: boolean;
  
  createdAt: string;
  updatedAt?: string;
}

// ============================================
// DTOs
// ============================================

export interface CreateToolRequestDTO {
  toolName: string;
  description: string;
  detailedDescription?: string;
  useCase: string;
  targetUsers: ('therapist' | 'patient' | 'both')[];
  frequency?: string;
  suggestedCategory?: ToolCategory;
  suggestedApproaches?: TherapeuticApproach[];
  references?: string[];
}

export interface UpdateToolRequestDTO {
  status?: RequestStatus;
  priority?: RequestPriority;
  internalNotes?: string;
  assignedTo?: string;
  estimatedDelivery?: string;
  adminResponse?: string;
}

export interface CreateToolDTO {
  name: string;
  slug: string;
  description: string;
  longDescription?: string;
  icon: string;
  category: ToolCategory;
  therapeuticApproaches: TherapeuticApproach[];
  allApproaches?: boolean;
  status?: ToolStatus;
  route?: string;
  features: string[];
  requiresPatient?: boolean;
  requiresSubscription?: boolean;
  minimumPlan?: 'free' | 'basic' | 'pro' | 'enterprise';
}

// ============================================
// CONSTANTS
// ============================================

export const TOOL_CATEGORIES_INFO: Record<ToolCategory, {
  name: string;
  description: string;
  icon: string;
}> = {
  [ToolCategory.ASSESSMENT]: {
    name: 'Evaluación',
    description: 'Herramientas de diagnóstico y evaluación',
    icon: '📋'
  },
  [ToolCategory.INTERVENTION]: {
    name: 'Intervención',
    description: 'Técnicas y métodos de intervención terapéutica',
    icon: '🎯'
  },
  [ToolCategory.VISUALIZATION]: {
    name: 'Visualización',
    description: 'Representación gráfica de datos terapéuticos',
    icon: '📊'
  },
  [ToolCategory.TRACKING]: {
    name: 'Seguimiento',
    description: 'Monitoreo del progreso y evolución',
    icon: '📈'
  },
  [ToolCategory.COMMUNICATION]: {
    name: 'Comunicación',
    description: 'Herramientas de comunicación terapeuta-paciente',
    icon: '💬'
  },
  [ToolCategory.PSYCHOEDUCATION]: {
    name: 'Psicoeducación',
    description: 'Material educativo para pacientes',
    icon: '📚'
  },
  [ToolCategory.SELF_HELP]: {
    name: 'Autoayuda',
    description: 'Prácticas y ejercicios para el paciente',
    icon: '🧘'
  },
  [ToolCategory.COACHING]: {
    name: 'Coaching',
    description: 'Herramientas de coaching y desarrollo personal',
    icon: '🏆'
  },
  [ToolCategory.MINDFULNESS]: {
    name: 'Mindfulness',
    description: 'Meditación y atención plena',
    icon: '🧘‍♀️'
  },
  [ToolCategory.OTHER]: {
    name: 'Otras',
    description: 'Otras herramientas terapéuticas',
    icon: '✨'
  }
};

export const REQUEST_STATUS_INFO: Record<RequestStatus, {
  name: string;
  description: string;
  color: string;
}> = {
  [RequestStatus.PENDING]: {
    name: 'Pendiente',
    description: 'Esperando revisión inicial',
    color: 'stone'
  },
  [RequestStatus.UNDER_REVIEW]: {
    name: 'En revisión',
    description: 'Siendo evaluada por el equipo',
    color: 'amber'
  },
  [RequestStatus.APPROVED]: {
    name: 'Aprobada',
    description: 'Aprobada para desarrollo',
    color: 'green'
  },
  [RequestStatus.IN_DEVELOPMENT]: {
    name: 'En desarrollo',
    description: 'Actualmente en desarrollo',
    color: 'blue'
  },
  [RequestStatus.COMPLETED]: {
    name: 'Completada',
    description: 'Herramienta disponible',
    color: 'emerald'
  },
  [RequestStatus.REJECTED]: {
    name: 'Rechazada',
    description: 'No se desarrollará',
    color: 'red'
  }
};

// ============================================
// HERRAMIENTAS INICIALES (SEED DATA)
// ============================================

export const INITIAL_THERAPEUTIC_TOOLS: Omit<TherapeuticTool, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>[] = [
  {
    name: 'Genograma Familiar',
    slug: 'genogram',
    description: 'Árbol genealógico terapéutico para visualizar relaciones familiares y patrones intergeneracionales',
    longDescription: 'El genograma es una herramienta fundamental de la terapia sistémica que permite representar gráficamente la estructura familiar multigeneracional, identificando patrones relacionales, eventos críticos, transmisión transgeneracional y roles familiares.',
    icon: '🌳',
    category: ToolCategory.VISUALIZATION,
    therapeuticApproaches: [
      'systemic' as TherapeuticApproach,
      'psychodynamic' as TherapeuticApproach,
      'integrative' as TherapeuticApproach,
      'narrative' as TherapeuticApproach
    ],
    allApproaches: false,
    status: ToolStatus.AVAILABLE,
    version: '1.0.0',
    route: '/patient/:patientId/genogram',
    features: [
      'Representación visual de miembros familiares',
      'Tipos de relaciones (fusionadas, distantes, conflictivas)',
      'Simbología estándar (género, fallecidos, paciente identificado)',
      'Guardado automático',
      'Exportación del genograma'
    ],
    requiresPatient: true,
    requiresSubscription: false,
    minimumPlan: 'free'
  },
  {
    name: 'Rueda de la Vida',
    slug: 'wheel-of-life',
    description: 'Herramienta de coaching para evaluar y visualizar el equilibrio en diferentes áreas de la vida',
    longDescription: 'La Rueda de la Vida es una herramienta fundamental del coaching que permite al cliente evaluar su nivel de satisfacción en diferentes áreas vitales, identificar desequilibrios y establecer metas de mejora.',
    icon: '🎡',
    category: ToolCategory.COACHING,
    therapeuticApproaches: [
      'humanistic' as TherapeuticApproach,
      'solution_focused' as TherapeuticApproach,
      'integrative' as TherapeuticApproach
    ],
    allApproaches: true,
    status: ToolStatus.AVAILABLE,
    version: '1.0.0',
    features: [
      'Evaluación de 8-12 áreas de vida',
      'Visualización en gráfico radar',
      'Historial de evaluaciones',
      'Notas reflexivas por área',
      'Definición de metas específicas'
    ],
    requiresPatient: true,
    requiresSubscription: false,
    minimumPlan: 'free'
  },
  {
    name: 'Notas de Sesión',
    slug: 'session-notes',
    description: 'Sistema de notas clínicas estructuradas para documentar sesiones terapéuticas',
    icon: '📝',
    category: ToolCategory.TRACKING,
    therapeuticApproaches: [],
    allApproaches: true,
    status: ToolStatus.AVAILABLE,
    version: '1.0.0',
    route: '/patient/:patientId/notes',
    features: [
      'Notas estructuradas por sesión',
      'Transcripción de voz',
      'Etiquetas y categorización',
      'Seguimiento de temas pendientes',
      'Resumen del proceso'
    ],
    requiresPatient: true,
    requiresSubscription: false,
    minimumPlan: 'free'
  },
  {
    name: 'Asignación de Prácticas',
    slug: 'practice-assignment',
    description: 'Sistema para asignar y hacer seguimiento de tareas terapéuticas entre sesiones',
    icon: '🧘',
    category: ToolCategory.SELF_HELP,
    therapeuticApproaches: [],
    allApproaches: true,
    status: ToolStatus.AVAILABLE,
    version: '1.0.0',
    route: '/patient/:patientId/practices',
    features: [
      'Biblioteca de prácticas',
      'Asignación personalizada',
      'Seguimiento de adherencia',
      'Feedback del paciente',
      'Estadísticas de progreso'
    ],
    requiresPatient: true,
    requiresSubscription: false,
    minimumPlan: 'free'
  }
];

